import { useState, useCallback } from 'react'
import {
  ADMIN_PASSWORD_HASH,
  SESSION_TTL_MS,
  SESSION_STORAGE_KEY,
} from '@/config/admin'

interface SessionRecord {
  authenticatedAt: number
}

function loadSession(): boolean {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return false
    const record = JSON.parse(raw) as SessionRecord
    return Date.now() - record.authenticatedAt < SESSION_TTL_MS
  } catch {
    return false
  }
}

async function hashPassword(password: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(password),
  )
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export function useAdminAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(loadSession)
  const [isChecking, setIsChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const login = useCallback(async (password: string) => {
    setIsChecking(true)
    setError(null)
    try {
      const hash = await hashPassword(password)
      if (hash === ADMIN_PASSWORD_HASH) {
        const record: SessionRecord = { authenticatedAt: Date.now() }
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(record))
        setIsAuthenticated(true)
      } else {
        setError('Incorrect password.')
      }
    } catch {
      setError('Authentication failed. Please try again.')
    } finally {
      setIsChecking(false)
    }
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
    setIsAuthenticated(false)
    setError(null)
  }, [])

  return { isAuthenticated, isChecking, error, login, logout }
}
